import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type BubbleColorsConstantType,
    type BubbleDynamicStyleType,
    type BubbleSizeConstantType,
    type BubbleLayoutStyle,
    type BubbleMeasures,
    type BubbleVariant,
    type BubbleColors,
    type BubbleSize
} from "./type";
import type {
    Mutable
} from "../../types";

export const BUBBLE_TYPOGRAPHIES: Record<BubbleSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    large: "bodyLargeSize",
    small: "bodySmallSize"
};

export const BUBBLE_PADDINGS: Record<BubbleSize, keyof NCoreUIKit.ControlPaddings> = {
    large: "medium",
    medium: "small",
    small: "small"
};

export const BUBBLE_LAYOUTS: Record<BubbleVariant, BubbleLayoutStyle> = {
    assistant: {
        alignSelf: "flex-start",
        maxWidth: "80%"
    },
    danger: {
        alignSelf: "flex-start",
        maxWidth: "80%"
    },
    user: {
        alignSelf: "flex-end",
        maxWidth: "80%"
    },
    plain: {
        alignSelf: "stretch",
        maxWidth: "100%"
    },
    system: {
        alignSelf: "center",
        maxWidth: "80%"
    }
};

export const BUBBLE_MAX_LINES = 8;

export const BUBBLE_QUICK_REACTIONS: Array<string> = [
    "👍",
    "❤️",
    "😂",
    "🎉",
    "👀",
    "🙏"
];

export const getBubbleSize = ({
    controlPaddings,
    controlHeights,
    typography,
    size
}: BubbleSizeConstantType): BubbleMeasures => {
    const currentTypography = BUBBLE_TYPOGRAPHIES[size];

    return {
        paddingHorizontal: controlPaddings[BUBBLE_PADDINGS[size]],
        lineHeight: typography[currentTypography].lineHeight,
        minHeight: controlHeights[size],
        typography: currentTypography
    };
};

export const getBubbleColors = ({
    isDarkTheme,
    isPressable,
    isDisabled,
    variant,
    colors
}: BubbleColorsConstantType): BubbleColors => {
    const baseColors: Record<BubbleVariant, BubbleColors> = {
        user: {
            containerColor: isDarkTheme ? colors.content.container.selected : colors.content.container.inverse,
            fadeColor: isDarkTheme ? colors.content.container.selected : colors.content.container.inverse,
            emphasisColor: isDarkTheme ? colors.content.text.high : colors.content.text.contrastHigh,
            textColor: isDarkTheme ? colors.content.text.high : colors.content.text.contrastHigh,
            borderColor: "transparent"
        },
        assistant: {
            containerColor: colors.content.container.raised,
            fadeColor: colors.content.container.raised,
            borderColor: colors.content.border.subtle,
            emphasisColor: colors.content.text.high,
            textColor: colors.content.text.high
        },
        danger: {
            emphasisColor: colors.system.onContainer.error,
            textColor: colors.system.onContainer.error,
            containerColor: colors.system.low.error,
            fadeColor: colors.system.low.error,
            borderColor: "transparent"
        },
        plain: {
            fadeColor: colors.content.container.default,
            emphasisColor: colors.content.text.high,
            textColor: colors.content.text.high,
            containerColor: "transparent",
            borderColor: "transparent"
        },
        system: {
            fadeColor: colors.content.container.default,
            borderColor: colors.content.border.subtle,
            emphasisColor: colors.content.text.high,
            textColor: colors.content.text.mid,
            containerColor: "transparent"
        }
    };

    const isChip = isPressable && (variant === "user" || variant === "assistant");

    const chipColors: Partial<BubbleColors> = isChip ? {
        containerColor: colors.content.container.raised,
        fadeColor: colors.content.container.raised,
        borderColor: colors.content.border.default,
        emphasisColor: colors.content.text.high,
        textColor: colors.content.text.high
    } : {};

    if(!isDisabled) {
        return {
            ...baseColors[variant],
            ...chipColors
        };
    }

    return {
        ...baseColors[variant],
        containerColor: colors.system.state.overlay.disabled.neutral,
        emphasisColor: colors.system.state.content.disabled.neutral,
        borderColor: colors.system.state.border.disabled.neutral,
        textColor: colors.system.state.content.disabled.neutral
    };
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "column",
        flexShrink: 1,
        minWidth: 0
    }
});

export const useStyles = ({
    variant
}: BubbleDynamicStyleType) => {
    const styles = {
        container: {
            ...BUBBLE_LAYOUTS[variant]
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
